import { ComponentMap } from '@cinch-nx/data-storyblok'
import { SbBlokData, storyblokEditable } from '@storyblok/react'
import { isEqual } from 'lodash'
import React from 'react'
import { ElementFactory } from './element-factory/element-factory'
import { logError } from './helpers/helpers'
import { ComponentLayouts } from './types'

interface StoryblokRendererProps {
  block: SbBlokData | SbBlokData[]
  components: ComponentLayouts | ComponentMap
  props?: { [key: string]: unknown }
  limitedItems?: { [key: string]: number }
}

const blockOverRenderingLimit = (
  block: SbBlokData,
  limitedItems: { [key: string]: number },
) => {
  if (!block.component || !(block.component in limitedItems)) {
    return false
  }

  const currentCount = limitedItems[block.component]
  if (currentCount <= 0) {
    return true
  }

  limitedItems[block.component] = currentCount - 1
  return false
}

/**
 * Renders Storyblok blocks into React components with editable attributes.
 *
 * This component takes Storyblok block data and renders it using the provided component mapping.
 * It supports rendering both single blocks and arrays of blocks and can limit the number of
 * times specific components are rendered.
 *
 * Rendering strategies:
 * - Direct component mapping (ComponentMap)
 * - Categorized components with optional wrapper configurations (ComponentLayouts)
 *   - noWrapper: Components rendered without any wrapper
 *   - standardWrapper: Components wrapped with standard Wrapper (1280px max-width)
 *   - slimWrapper: Components wrapped with slim Wrapper (1056px max-width)
 *   - fullWidthWrapper: Components wrapped with fluid Wrapper (full-width)
 *
 * @param block - A single Storyblok block or an array of blocks to render.
 * @param components - A mapping of Storyblok component names to React components.
 * @param props - Optional additional props to pass to the rendered components.
 * @param limitedItems - Optional object specifying maximum render counts for specific component types.
 * @returns A React element containing the rendered Storyblok blocks, or `null` if no valid blocks exist.
 */
const StoryblokRenderer = ({
  block,
  components,
  props,
  limitedItems,
}: StoryblokRendererProps): React.JSX.Element | null => {
  const renderCounts = limitedItems ? { ...limitedItems } : {}

  if (Array.isArray(block)) {
    return (
      <>
        {block.map((blok) => {
          if (blockOverRenderingLimit(blok, renderCounts)) {
            return null
          }

          try {
            const blockElement = ElementFactory(blok, components, props)
            if (!blockElement) {
              return null
            }

            return (
              <div {...storyblokEditable(blok)} key={blok._uid}>
                {blockElement}
              </div>
            )
          } catch (error) {
            logError('Failed to render block:', blok.component, error)
            return null
          }
        })}
      </>
    )
  }

  if (blockOverRenderingLimit(block, renderCounts)) {
    return null
  }

  try {
    const blockElement = ElementFactory(block, components, props)
    if (!blockElement) {
      return null
    }

    return <div {...storyblokEditable(block)}>{blockElement}</div>
  } catch (error) {
    logError('Failed to render block:', block.component, error)
    return null
  }
}

export default React.memo(
  StoryblokRenderer,
  (prevProps: StoryblokRendererProps, nextProps: StoryblokRendererProps) =>
    prevProps.block === nextProps.block &&
    prevProps.components === nextProps.components &&
    isEqual(prevProps.limitedItems, nextProps.limitedItems) &&
    isEqual(prevProps.props, nextProps.props),
)
